import { useEffect, useMemo, useState } from "react";

import { Button } from "../ui";

/**
 * Choose which physical shops raise a restock alert.
 *
 * A store can report a couple of hundred shops per product, so alerting on
 * all of them would be unusable — nothing is announced until shops are picked
 * here. Per-shop stock is still recorded and shown on the product page
 * regardless; this only gates notifications.
 *
 * The list is each store's own shop directory, plus any shop the watch
 * tick has reported stock for that the directory doesn't name.
 */

type KnownStore = {
	warehouseCode: string;
	name: string;
	city: string | null;
	/** Which retailer the shop belongs to; null for a shop only known from
	 *  recorded stock rather than from the store's own directory. */
	storeId: string | null;
};

export function StoreAlertPicker({
	embedded = false,
	onSaved,
}: {
	/** Inside a Settings row: no frame, heading or explanation of its own. */
	embedded?: boolean;
	onSaved?: (count: number) => void;
} = {}) {
	const [stores, setStores] = useState<KnownStore[] | null>(null);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [filter, setFilter] = useState("");
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		Promise.all([
			fetch("/api/store-locations").then((r) => r.json()),
			fetch("/api/settings/store-alerts").then((r) => r.json()),
		])
			.then(([list, chosen]) => {
				if (cancelled) return;
				setStores((list as { stores: KnownStore[] }).stores);
				setSelected(new Set((chosen as { warehouseCodes: string[] }).warehouseCodes));
			})
			.catch(() => {
				if (!cancelled) setError("Could not load shops.");
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const visible = useMemo(() => {
		if (!stores) return [];
		const q = filter.trim().toLowerCase();
		if (!q) return stores;
		return stores.filter(
			(s) =>
				s.name.toLowerCase().includes(q) ||
				(s.city ?? "").toLowerCase().includes(q) ||
				(s.storeId ?? "").toLowerCase().includes(q),
		);
	}, [stores, filter]);

	function toggle(code: string): void {
		setSaved(null);
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(code)) next.delete(code);
			else next.add(code);
			return next;
		});
	}

	async function save(): Promise<void> {
		setSaving(true);
		setError(null);
		try {
			const res = await fetch("/api/settings/store-alerts", {
				method: "PUT",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ warehouseCodes: [...selected] }),
			});
			if (!res.ok) throw new Error();
			onSaved?.(selected.size);
			setSaved(
				selected.size === 0
					? "Saved — no shop alerts will be sent."
					: `Saved — alerting on ${selected.size} shop${selected.size === 1 ? "" : "s"}.`,
			);
		} catch {
			setError("Could not save.");
		} finally {
			setSaving(false);
		}
	}

	const Frame = embedded ? "div" : "section";
	return (
		<Frame className={embedded ? "" : "rounded-[10px] border border-hairline bg-paper p-3"}>
			{!embedded && <h2 className="mb-1 text-[13px] font-semibold text-ink">In-store restock alerts</h2>}
			{!embedded && (
				<p className="mb-2.5 max-w-prose text-xs text-muted">
					Get an alert when a product you watch comes back on the shelf in one of these physical shops, Nille and Ringo.
					Shop stock is checked each time a watched product is checked, and when you open a product's page. With none
					picked, no in-store alerts are sent, though shop stock is still shown on product pages. Ringo only answers for
					one shop at a time, so for Ringo these picks also decide which shops are checked at all. Filter by shop, city
					or store.
				</p>
			)}

			{stores === null && !error && <p className="text-xs text-muted">Loading…</p>}

			{stores !== null && stores.length === 0 && (
				<p className="rounded-[10px] border border-dashed border-line px-3 py-2.5 text-xs text-muted">
					No shops available. None of the enabled stores publishes a shop directory, or they could not be reached.
				</p>
			)}

			{stores !== null && stores.length > 0 && (
				<>
					<div className="mb-2 flex flex-wrap items-center gap-2">
						<input
							type="search"
							placeholder="Filter by shop or city…"
							value={filter}
							onChange={(e) => setFilter(e.target.value)}
							className="h-8 w-full min-w-0 rounded-md border border-hairline bg-ground px-2.5 text-[13px] text-ink placeholder:text-muted hover:border-line sm:max-w-xs sm:flex-1 pointer-coarse:h-10"
						/>
						<span className="text-[11px] text-muted">
							{selected.size} of {stores.length} selected
						</span>
					</div>

					{/* Two columns of shops from md up, so a wide screen shows twice
              as many before the list scrolls. */}
					<ul
						className={`mb-2.5 grid content-start gap-px overflow-y-auto rounded-md border border-hairline bg-ground p-1 md:grid-cols-2 ${
							embedded ? "max-h-60" : "max-h-72"
						}`}
					>
						{visible.map((s) => (
							<li key={s.warehouseCode} className="min-w-0">
								<label
									className={`flex cursor-pointer items-center gap-2.5 rounded-md px-2 hover:bg-raised ${
										embedded ? "min-h-tap py-0.5" : "min-h-tap py-1"
									}`}
								>
									<input
										type="checkbox"
										checked={selected.has(s.warehouseCode)}
										onChange={() => toggle(s.warehouseCode)}
										className="size-3.5 shrink-0 accent-ink"
									/>
									<span className="min-w-0 flex-1 truncate text-[13px] text-ink">
										{s.name}
										{s.city ? <span className="text-muted"> · {s.city}</span> : null}
									</span>
									{s.storeId && (
										<span className="shrink-0 rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted">
											{s.storeId}
										</span>
									)}
									<span className="shrink-0 font-mono text-[10px] text-muted">{s.warehouseCode}</span>
								</label>
							</li>
						))}
						{visible.length === 0 && (
							<li className="col-span-full px-3 py-3 text-center text-xs text-muted">No shops match “{filter}”.</li>
						)}
					</ul>

					<div className="flex flex-wrap items-center gap-2">
						<Button variant="primary" onClick={save} disabled={saving}>
							{saving ? "Saving…" : "Save"}
						</Button>
						{selected.size > 0 && (
							<Button
								variant="secondary"
								onClick={() => {
									setSelected(new Set());
									setSaved(null);
								}}
							>
								Clear all
							</Button>
						)}
						{saved && <span className="text-xs text-muted">{saved}</span>}
					</div>
				</>
			)}

			{error && <p className="mt-2 text-xs text-muted">{error}</p>}
		</Frame>
	);
}
